import { calcularPagoSemanal, formatearMoneda, type Boveda, type NominaPersona } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { Button } from '@/components/ui/button';
import { Campo, Input } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { fecha } from '@/lib/utils';
import { avisarError } from '@/lib/avisos';
import { SelectorBoveda } from '../boveda/SelectorBoveda';

type Accion = 'descontar' | 'perdonar';

/** Revisión y pago semanal: el dueño decide descontar o perdonar cada faltante de caja. */
export function PagoDialog({ persona, semana, onCerrar }: { persona: NominaPersona; semana: string; onCerrar: () => void }) {
  const qc = useQueryClient();
  const [acciones, setAcciones] = useState<Record<string, Accion>>(() => Object.fromEntries(persona.faltantesPendientes.map((f) => [f.id, 'descontar'])));
  const [ajuste, setAjuste] = useState('');
  const [nota, setNota] = useState('');
  const [boveda, setBoveda] = useState<Boveda | ''>('');
  const { puede } = useSesion();
  const puedePagar = puede('boveda.retirar');

  const descuento = persona.faltantesPendientes.filter((f) => acciones[f.id] === 'descontar').reduce((s, f) => s + f.montoMXN, 0);
  const ajustes = Math.round((Number.parseFloat(ajuste) || 0) * 100);
  const c = calcularPagoSemanal({
    sueldoSemanal: persona.sueldoSemanal,
    minutosTrabajados: persona.minutosTrabajados,
    minutosProgramados: persona.minutosProgramados,
    comisionesServicios: persona.comisionServicios,
    comisionesProductos: persona.comisionProductos,
    propinas: persona.propinasDirectas + persona.propinasReparto,
    faltantesADescontar: descuento,
    ajustes,
  });

  const pagar = useMutation({
    mutationFn: () =>
      api('/nomina/pagar', {
        method: 'POST',
        body: { boveda, semana, usuarioId: persona.usuario.id, faltantes: Object.entries(acciones).map(([id, accion]) => ({ id, accion })), ajustes, nota: nota || undefined },
      }),
    onSuccess: () => {
      toast.success(`Pago de ${persona.usuario.nombre} registrado`);
      void qc.invalidateQueries({ queryKey: ['nomina'] });
      onCerrar();
    },
    onError: (e) => avisarError(e),
  });

  const fila = (etiqueta: string, valor: number, negativo = false) => (
    <div className="flex justify-between py-1">
      <dt className="text-foreground-muted">{etiqueta}</dt>
      <dd className={negativo && valor ? 'text-destructive' : ''}>{negativo && valor ? `−${formatearMoneda(valor)}` : formatearMoneda(valor)}</dd>
    </div>
  );

  return (
    <Dialogo
      abierto
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={`Pago de ${persona.usuario.nombre}`}
      descripcion={`Semana del ${fecha(`${semana}T12:00:00-06:00`)}`}
      pie={
        <Button onClick={() => pagar.mutate()} cargando={pagar.isPending} disabled={c.total < 0 || !boveda || !puedePagar}>
          Registrar pago de {formatearMoneda(Math.max(0, c.total))}
        </Button>
      }
    >
      <dl className="cifras divide-y divide-border/60 text-[14px]">
        {persona.sueldoSemanal != null && fila('Sueldo semanal', c.sueldoBase)}
        {c.ajusteTiempo !== 0 && fila('Tiempo trabajado de menos', -c.ajusteTiempo, true)}
        {fila(`Comisión por servicios (${persona.servicios})`, c.comisionServicios)}
        {fila('Comisión por productos', c.comisionProductos)}
        {fila('Propinas', c.propinas)}
      </dl>

      {persona.faltantesPendientes.length > 0 && (
        <fieldset className="mt-4 rounded-lg border border-destructive/30 bg-destructive-soft/40 p-3">
          <legend className="px-1 text-[13px] font-medium text-destructive">Faltantes de caja pendientes</legend>
          {persona.faltantesPendientes.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-[13.5px]" role="radiogroup" aria-label={`Faltante del ${fecha(f.fecha)}`}>
              <span className="cifras">
                {fecha(f.fecha)} · {formatearMoneda(f.monto, f.moneda)}
              </span>
              <span className="flex gap-3">
                {(['descontar', 'perdonar'] as const).map((a) => (
                  <label key={a} className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name={`faltante-${f.id}`}
                      checked={acciones[f.id] === a}
                      onChange={() => setAcciones((x) => ({ ...x, [f.id]: a }))}
                      className="accent-[var(--bb-accent)]"
                    />
                    {a === 'descontar' ? 'Descontar' : 'Perdonar'}
                  </label>
                ))}
              </span>
            </div>
          ))}
        </fieldset>
      )}

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Ajuste manual (MXN)" ayuda="Bono (+) o descuento (−)">
          {(p) => <Input {...p} type="number" step="0.01" value={ajuste} onChange={(e) => setAjuste(e.target.value)} className="cifras" />}
        </Campo>
        <Campo etiqueta="Nota">{(p) => <Input {...p} value={nota} onChange={(e) => setNota(e.target.value)} />}</Campo>
        <SelectorBoveda valor={boveda} onCambiar={setBoveda} />
        {!puedePagar && <p className="text-[12.5px] text-warning">Para pagar nómina necesitas el permiso de retirar de la bóveda</p>}
      </div>

      <div className="mt-4 flex items-baseline justify-between rounded-lg bg-primary px-4 py-3 text-on-primary">
        <span className="text-[13px] uppercase tracking-wider opacity-70">Total a pagar</span>
        <span data-testid="total-pago" className="cifras font-display text-[28px]">
          {formatearMoneda(c.total)}
        </span>
      </div>
    </Dialogo>
  );
}
